import React, {useEffect} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import { UploadProvider } from './context/UploadContext'; // Import the new provider
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import SessionMap from './pages/SessionMap';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import AnalysisPage from './pages/AnalysisPage';
import PayAnalysisPage from './pages/PayAnalysisPage';
import SessionPage from './pages/SessionPage';
import CompanySettings from './pages/CompanySettings';
import GlobalPayTables from './pages/settings/GlobalPayTables';
import IndotPayTable from './pages/settings/paytables/indot';
import IadotPayTable from './pages/settings/paytables/iadot';
import UploadPage from './pages/UploadPage';
import DownloadPage from './pages/DownloadPage';
import FeatureHighlight from './pages/FeatureHighlight';
import SharedSessionPage from './pages/SharedSessionPage';
import FileDownloadPage from './pages/FileDownloadPage';
import ShareLinks from './pages/ShareLink';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscriptionRequiredPage from './pages/SubscriptionRequiredPage';
import NascarHeatmapPage from './pages/NascarHeatmapPage';
import { RacingWorkspace, RacingHome, RacingProjects, RacingProject } from './racing/RacingWorkspace';
import RacingUpload from './racing/RacingUpload';
import RacingAnalyzer from './racing/RacingAnalyzer';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔥 Error Boundary caught an error:', error);
    console.error('🔥 Error Info:', errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'left',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
          fontFamily: 'monospace'
        }}>
          <h2>Something went wrong</h2>
          <details style={{ whiteSpace: 'pre-wrap', marginTop: '20px' }}>
            <summary>Click to see error details</summary>
            <br />
            <strong>Error:</strong>
            <br />
            {this.state.error ? this.state.error.toString() : 'No error details available'}
            <br />
            <br />
            <strong>Component Stack:</strong>
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack 
              ? this.state.errorInfo.componentStack 
              : 'No component stack available'}
          </details>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  useEffect(() => {
  
  return () => {
  };
}, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <UserProvider>
            <UploadProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/share/:token" element={<SharedSessionPage />} />
                <Route path="/file/:token" element={<FileDownloadPage />} />
                <Route path="/download" element={<DownloadPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/feature-highlight" element={<FeatureHighlight />} />

                <Route path="/home" element={<ProtectedRoute currentPage="Home"><Dashboard /></ProtectedRoute>} />
                <Route path="/map" element={<Navigate to="/home?view=map" replace />} />
                <Route path="/projects" element={<ProtectedRoute currentPage="Projects"><Projects /></ProtectedRoute>} />
                <Route path="/projects/:projectId" element={<ProtectedRoute currentPage="Projects"><ProjectDetail /></ProtectedRoute>} />
                <Route path="/session/:sessionId" element={<ProtectedRoute currentPage="Projects"><SessionPage /></ProtectedRoute>} />
                <Route path="/session/:sessionId/map" element={<ProtectedRoute currentPage="Projects"><SessionMap /></ProtectedRoute>} />
                <Route path="/upload" element={<ProtectedRoute currentPage="Upload"><UploadPage /></ProtectedRoute>} />
                <Route path="/analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
                <Route path="/pay-analysis/:projectId" element={<ProtectedRoute><PayAnalysisPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute currentPage="Settings"><CompanySettings /></ProtectedRoute>} />
                <Route path="/settings/pay-tables" element={<ProtectedRoute><GlobalPayTables /></ProtectedRoute>} />
                <Route path="/settings/pay-tables/indot" element={<ProtectedRoute><IndotPayTable /></ProtectedRoute>} />
                <Route path="/settings/pay-tables/iadot" element={<ProtectedRoute><IadotPayTable /></ProtectedRoute>} />
                <Route path="/subscription-required" element={<ProtectedRoute currentPage="SubscriptionRequired"><SubscriptionRequiredPage /></ProtectedRoute>} />
                <Route path="/share-links" element={<ProtectedRoute currentPage="Share Links"><ShareLinks /></ProtectedRoute>} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/racing" element={<RacingWorkspace />}>
                  <Route index element={<RacingHome />} />
                  <Route path="projects" element={<RacingProjects />} />
                  <Route path="projects/:projectId" element={<RacingProject />} />
                  <Route path="upload" element={<RacingUpload />} />
                  <Route path="session/:sessionId" element={<RacingAnalyzer />} />
                  <Route path="settings" element={<CompanySettings />} />
                </Route>
                <Route path="/nascar/iowa" element={<NascarHeatmapPage />} />

              

                <Route path="*" element={<NotFound />} />
              </Routes>
            </UploadProvider>
          </UserProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}
